import React, {
  lazy, memo, Suspense, useCallback, useEffect, useMemo, useState,
} from 'react'

import {
  Badge,
  Icon,
  Button,
  ChevronDown,
  MoreFilters as MoreFiltersIcon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { POPOVER_FEATURES } from 'Pods/SearchPage/Shared/FeaturesContent/constants'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { clearDatePublished } from 'Pods/SearchPage/Store/actions/datePublished'
import { clearDesigners } from 'Pods/SearchPage/Store/actions/designers'
import { addFileType, removeFileType, clearFileTypes } from 'Pods/SearchPage/Store/actions/fileTypes'
import { clearLicenses } from 'Pods/SearchPage/Store/actions/licenses'
import { clearMoreFilters } from 'Pods/SearchPage/Store/actions/moreFilters'
import { clearPolygon } from 'Pods/SearchPage/Store/actions/polygon'
import { clearPricing } from 'Pods/SearchPage/Store/actions/pricing'
import { clearQualityContent } from 'Pods/SearchPage/Store/actions/qualityContent'
import { clearSimulationReady } from 'Pods/SearchPage/Store/actions/simulationReady'
import { clearSize } from 'Pods/SearchPage/Store/actions/size'
import { clearStyles } from 'Pods/SearchPage/Store/actions/styles'
import { clearTags } from 'Pods/SearchPage/Store/actions/tags'
import { clearThreeDOnly } from 'Pods/SearchPage/Store/actions/threeDOnly'
import { clearThreeDprintOnly } from 'Pods/SearchPage/Store/actions/threeDPrintOnly'
import {
  ADVANCED_FILTER, ASSET_TYPE, DATE_PUBLISHED, DEFAULT_STATE, LICENSE_TYPE, QUALITY_LEVEL,
} from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import { getFiltersCount, getSecondaryFiltersCount } from 'Pods/SearchPage/Store/selectors'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'
import useMediaQuery from 'Utils/useMediaQuery'

import Columns from './Components/Columns/Columns'
import StackedSections from './Components/StackedSections/StackedSections'

// Keeps the drawer (and its CollapsibleSection subtree) out of the initial search pack. Safe to
// suspend: it renders only after the compact-breakpoint trigger is clicked, so this boundary is
// never reached during the page's SSR pass. Mirrors the CustomRange split in DatePublishedContent.
const MoreFiltersDrawer = lazy(() => import(
  /* webpackChunkName: "search-more-filters-drawer" */ 'Pods/LazyLoaded/MoreFiltersDrawer/MoreFiltersDrawer'
))

const prefixIcon = <Icon notSpaced svgComponent={MoreFiltersIcon} color="dark" size="small" />

const trailIcon = (
  <Icon
    notSpaced
    svgComponent={ChevronDown}
    color="darker"
    size="small"
    className="more-filters__trigger-chevron"
  />
)

// Below lg row 1 has no dropdowns (Figma 415:23304, 415:23612), so every filter opens in the
// All Filters drawer behind a "Filters" button: a side sheet at md, full screen below it.
const COMPACT_BREAKPOINTS = ['xs', 'sm', 'md']
// Mirror $main-filters-breakpoints in search_page.scss: below them the Quality / Date published
// dropdowns are hidden from the bar. Neither is a useBreakpoint token.
const QUALITY_OVERFLOW_QUERY = '(max-width: 1439.98px)'
const DATE_OVERFLOW_QUERY = '(max-width: 1959.98px)'
const DEFAULT_LICENSES = [LICENSE_TYPE.ALL]
const DEFAULT_QUALITY = [QUALITY_LEVEL.ANY]

function MoreFilters() {
  const translate = useTranslate()

  const advancedFilters = useSearchPageStore(state => state.advancedFilters)
  const hasCgtStandard = advancedFilters.includes(ADVANCED_FILTER.CGT_STANDARD_ONLY)
  const personalFilter = useSearchPageStore(state => state.personalFilter)
  const purchaseFilter = useSearchPageStore(state => state.purchaseFilter)
  const fileTypes = useSearchPageStore(state => state.fileTypes ?? EMPTY_ARRAY)
  const onSale = useSearchPageStore(state => state.onSale)
  const withSubscription = useSearchPageStore(state => state.withSubscription)
  const priceRanges = useSearchPageStore(state => state.priceRanges ?? EMPTY_ARRAY)
  const minPrice = useSearchPageStore(state => state.minPrice ?? '')
  const maxPrice = useSearchPageStore(state => state.maxPrice ?? '')
  const licenses = useSearchPageStore(state => state.licenses ?? DEFAULT_LICENSES)
  const datePublished = useSearchPageStore(state => state.datePublished ?? DATE_PUBLISHED.ALL_TIME)
  const quality = useSearchPageStore(state => state.quality ?? DEFAULT_QUALITY)
  const contentSource = useSearchPageStore(state => state.contentSource ?? DEFAULT_STATE.contentSource)
  const designers = useSearchPageStore(state => state.designers ?? EMPTY_ARRAY)
  const designerScopes = useSearchPageStore(state => state.designerScopes ?? EMPTY_ARRAY)
  const hasSecondaryFilters = useSearchPageStore(state => getSecondaryFiltersCount(state) > 0)
  const hasTags = useSearchPageStore(state => (state.tags ?? EMPTY_ARRAY).length > 0)
  const is3DOnly = useSearchPageStore(state => state.assetType === ASSET_TYPE.THREE_D_ONLY)
  const popoverFeaturesCount = useSearchPageStore(
    state => POPOVER_FEATURES.filter(feature => feature.select(state)).length,
  )
  // Below 480 the Clear button is hidden, so the compact trigger carries the count as a badge.
  const activeFiltersCount = useSearchPageStore(state => getFiltersCount(state))
  const breakpoint = useBreakpoint()
  // Gate the popover/modal split until mounted so the first client render matches SSR
  // (useBreakpoint is undefined on the server), avoiding a hydration mismatch.
  const [hasMounted, setHasMounted] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  const isCompact = hasMounted && COMPACT_BREAKPOINTS.includes(breakpoint)
  const isDesktop = hasMounted && !isCompact
  // Each filter whose row 1 slot is hidden at this width overflows into this popover to stay
  // reachable: Quality below 1440, Date published below 1960 (see main-filters__slot--* in
  // search_page.scss). License has no row 1 slot, so it is always here.
  const isBelowQualityBreakpoint = useMediaQuery(QUALITY_OVERFLOW_QUERY)
  const isBelowDateBreakpoint = useMediaQuery(DATE_OVERFLOW_QUERY)
  const showQualityOverflow = isDesktop && isBelowQualityBreakpoint
  const showLicenseOverflow = isDesktop
  const showDateOverflow = isDesktop && isBelowDateBreakpoint
  // Figma 2805:153402 keeps Designer inside More filters at every desktop width.
  const showDesignerSection = isDesktop
  // From xl the sections sit side by side (Figma 2807:161222, 2807:160650); lg stacks them in one
  // scrollable column under the overflowed Quality and License groups (Figma 3016:128923, 3016:129654).
  const isColumnLayout = isDesktop && breakpoint !== 'lg'

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  useEffect(() => {
    if (!isCompact && isDrawerOpen) {
      setIsDrawerOpen(false)
    }
  }, [isCompact, isDrawerOpen])

  // Filters owned by the More Filters popover itself; drives the desktop trigger and its Reset.
  // CGT Standard shares advancedFilters but is rendered in the Quality group.
  const hasOwnFilters = useMemo(() => {
    return (
      advancedFilters.some(filter => filter !== ADVANCED_FILTER.CGT_STANDARD_ONLY) ||
      personalFilter != null ||
      purchaseFilter != null
    )
  }, [advancedFilters, personalFilter, purchaseFilter])

  // On compact breakpoints the trigger opens the All Filters modal, which also hosts
  // File Format, Price and Date Published — those count toward the trigger's active state there only.
  const hasModalFilters = useMemo(() => {
    return (
      hasOwnFilters ||
      fileTypes.length > 0 ||
      onSale ||
      withSubscription ||
      priceRanges.length > 0 ||
      minPrice !== '' ||
      maxPrice !== '' ||
      licenses[0] !== LICENSE_TYPE.ALL ||
      quality[0] !== QUALITY_LEVEL.ANY ||
      contentSource !== DEFAULT_STATE.contentSource ||
      hasCgtStandard ||
      datePublished !== DATE_PUBLISHED.ALL_TIME ||
      designers.length > 0 ||
      designerScopes.length > 0 ||
      hasTags ||
      hasSecondaryFilters
    )
  }, [
      hasOwnFilters,
      fileTypes,
      onSale,
      withSubscription,
      priceRanges,
      minPrice,
      maxPrice,
      licenses,
      quality,
      contentSource,
      hasCgtStandard,
      datePublished,
      designers,
      designerScopes,
      hasTags,
      hasSecondaryFilters,
    ])

  // "+n" on the desktop trigger (Figma 2989:109667): every value that is on inside the popover at
  // this width, counted the way "Clear (n)" counts them (getFiltersCount). Filters from hidden row 1
  // dropdowns count only while they overflow into the popover.
  const popoverFiltersCount = useMemo(() => {
    let count = advancedFilters.filter(filter => filter !== ADVANCED_FILTER.CGT_STANDARD_ONLY).length
      + (personalFilter != null ? 1 : 0)
      + (purchaseFilter != null ? 1 : 0)

    if (is3DOnly) count += popoverFeaturesCount
    if (showQualityOverflow) {
      count += quality.filter(level => level !== QUALITY_LEVEL.ANY).length
        + (contentSource !== DEFAULT_STATE.contentSource ? 1 : 0)
        + (hasCgtStandard ? 1 : 0)
    }
    if (showLicenseOverflow) {
      count += licenses.filter(license => license !== LICENSE_TYPE.ALL).length
    }
    if (showDateOverflow && datePublished !== DATE_PUBLISHED.ALL_TIME) count += 1
    if (showDesignerSection) count += designers.length + designerScopes.length

    return count
  }, [
    advancedFilters,
    personalFilter,
    purchaseFilter,
    is3DOnly,
    popoverFeaturesCount,
    showQualityOverflow,
    quality,
    contentSource,
    hasCgtStandard,
    showLicenseOverflow,
    licenses,
    showDateOverflow,
    datePublished,
    showDesignerSection,
    designers,
    designerScopes,
  ])

  const hasPopoverFilters = popoverFiltersCount > 0

  const handleReset = useCallback(() => {
    clearMoreFilters()
    if (is3DOnly) {
      POPOVER_FEATURES.forEach(feature => feature.onChange(false))
    }
    if (showQualityOverflow) {
      clearQualityContent()
    }
    if (showLicenseOverflow) {
      clearLicenses()
    }
    if (showDateOverflow) {
      clearDatePublished()
    }
    if (showDesignerSection) {
      clearDesigners()
    }
  }, [is3DOnly, showQualityOverflow, showLicenseOverflow, showDateOverflow, showDesignerSection])

  const handleFileTypeChange = useCallback((_, value, isOn) => {
    if (isOn) {
      addFileType(value)
    } else {
      removeFileType(value)
    }
  }, [])

  const handleModalReset = useCallback(() => {
    clearFileTypes()
    clearPricing()
    clearLicenses()
    clearQualityContent()
    clearDatePublished()
    clearMoreFilters()
    clearDesigners()
    // Features section (3D-only): clears the technical attributes + the low-poly polygon range.
    clearThreeDOnly()
    clearPolygon()
    // The drawer's Features section swaps in PRINT_FEATURES for a 3D-print search and hides
    // their section-level reset, so this footer Reset is their only clear path.
    clearThreeDprintOnly()
    clearStyles()
    clearSize()
    clearTags()
    clearSimulationReady()
  }, [])

  const handleOpenDrawer = useCallback(() => {
    setIsDrawerOpen(true)
  }, [])

  const content = useMemo(() => (
    <>
      {isColumnLayout ? (
        <Columns showDatePublished={showDateOverflow} />
      ) : (
        <StackedSections
          showQualityOverflow={showQualityOverflow}
          showLicenseOverflow={showLicenseOverflow}
          showDateOverflow={showDateOverflow}
          showDesignerSection={showDesignerSection}
        />
      )}
      <Button
        type="ghost"
        size="tiny"
        customClass="filter-wrapper__reset-button"
        onClick={handleReset}
        disabled={!hasPopoverFilters}
      >
        {translate('search_page.filters.more_filters.reset')}
      </Button>
    </>
  ), [
    handleReset,
    hasPopoverFilters,
    isColumnLayout,
    showQualityOverflow,
    showLicenseOverflow,
    showDateOverflow,
    showDesignerSection,
  ])

  const hasActiveFilters = activeFiltersCount > 0
  // Figma 415:23304 / 415:23612 label the compact trigger "Filters" at every width.
  const triggerLabel = translate('search_page.filters.more_filters.trigger_label')
  const triggerLabelWithCount = translate(
    'search_page.filters.more_filters.trigger_label_with_count', { count: activeFiltersCount },
  )

  // The breakpoint is unknown until mount, so the server renders both triggers and CSS shows the
  // one for the viewport (more-filters__trigger / more-filters__desktop in search_page.scss).
  // Swapping one for the other on hydration reflowed the mobile filter row and shifted the grid.
  const showCompactTrigger = !hasMounted || isCompact
  const showDesktopTrigger = !hasMounted || isDesktop

  return (
    <>
      {showCompactTrigger && (
        <Button
          type="secondary"
          variant="with-icon"
          icon={prefixIcon}
          isActive={hasModalFilters}
          onClick={handleOpenDrawer}
          customClass="filter-wrapper__button more-filters__trigger"
          aria-label={hasActiveFilters ? triggerLabelWithCount : triggerLabel}
          aria-expanded={isDrawerOpen}
          trailIcon={trailIcon}
        >
          <span className="more-filters__trigger-label">
            {triggerLabel}
          </span>
          {hasActiveFilters && (
            <Badge type="primary" className="more-filters__trigger-badge">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
      )}
      {showDesktopTrigger && (
        <FiltersWrapper
          trailIconSize="small"
          placeholder={translate('search_page.filters.more_filters.desktop_title')}
          prefixIcon={prefixIcon}
          content={content}
          hasSelectedAnyFilters={hasPopoverFilters}
          selectedCount={popoverFiltersCount}
          positionHorizontal="left"
          closingDropdownClassName="filter-wrapper main-filters__fixed-dropdown more-filters__desktop"
          contentClassName={isColumnLayout
            ? 'more-filters__popover-content more-filters__popover-content--columns'
            : 'more-filters__popover-content'}
        />
      )}
      {isCompact && isDrawerOpen && (
        <Suspense fallback={null}>
          <MoreFiltersDrawer
            onClose={handleCloseDrawer}
            onReset={handleModalReset}
            fileTypes={fileTypes}
            onFileTypeChange={handleFileTypeChange}
          />
        </Suspense>
      )}
    </>
  )
}

export default memo(MoreFilters)
